import React from 'react';
import { useCheckoutRedirectToMenuCheck } from '@templates/Checkout/common/hooks/useCheckoutRedirectToMenuCheck';
import {
  useOrderTimeChangeToast,
  useIsScanAndPayPage,
  useIsDineIn,
  useOrderType,
} from '@olo-web/utils/common/hooks';
import { useUnavailabilityAlert } from '@domain/merchants/hooks/useUnavailabilityAlert';
import { Checkout } from './Checkout.ui';
import { ProductTotals } from '@olo-web/components/molecules/ProductTotals';
import { ApplyOffersButton } from './ApplyOffersButton';
import { TipsSection } from '@olo-web/components/atoms/TipsSection/TipsSection.ui';
import { CheckoutBagContainer } from './CheckoutBagContainer';
import { GridItem, SimpleGrid, VStack, Heading, Skeleton } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';
import { CheckoutOrderDetails } from './CheckoutOrderDetails';
import { CheckoutContactInfo } from './CheckoutContactInfo';
import { CheckoutPayment } from './CheckoutPayment';
import { CheckoutDisclaimer, CheckoutOrderButton } from './common';
import { usePaymentMethodValues } from '@olo-web/domain/payments/hooks';
import { useCustomer } from '@olo-web/domain/customer';
import { useMerchant } from '@domain/merchants/queries/useMerchant';
import { useContactInfoState, useScreenSizeState, useCheckoutState } from '@olo-web/client-state';
import { CheckoutDinein } from '../Checkout/CheckoutDinein.ui';
import { OnPremisePayment } from './CheckoutOnPremiseOnly/OnPremisePayment.ui';
import { useIsOnPremisePaymentOnly } from './common/hooks/useIsOnPremisePaymentOnly';
import { EOrderTypes } from '@olo-web/types/enums';

const SP = 6;

export const CheckoutOnline = () => {
  const { isMdDown } = useScreenSizeState();
  const { data: customerData } = useCustomer();
  const contactInfo = useContactInfoState();
  const { data: merchant } = useMerchant();
  const isOrderNoteEnabled = merchant?.ordernotesenabled;
  const { giftCardCoversEntireOrderAmount, isCash, isGooglePay, isApplePay, isCreditCard } =
    usePaymentMethodValues();
  const customer = customerData || contactInfo;
  const userMustLogin = !merchant?.allowGuestCheckout && !customerData?.customerId;
  const hideBelowContactInfo =
    (giftCardCoversEntireOrderAmount && !customer) ||
    ((!customer || userMustLogin) && !(isGooglePay || isApplePay) && isMdDown);
  const shouldShowGiftCard = !isCash && merchant?.giftCardPaymentEnabled;
  const shouldShowPaymentMethod = !giftCardCoversEntireOrderAmount;
  const isScanAndPayPage = useIsScanAndPayPage();
  const shouldShowOrderButton = isMdDown && !hideBelowContactInfo;
  const { addedPayment, selectedCard } = useCheckoutState();
  const isOnPremisePaymentOnly = useIsOnPremisePaymentOnly();
  const orderType = useOrderType();
  const disableContactInfo =
    !isOnPremisePaymentOnly &&
    isCreditCard &&
    !addedPayment &&
    !selectedCard &&
    !giftCardCoversEntireOrderAmount; // show for saved card and for when gift card covers whole amount but user had selected CC and didnt add payment
  const isDineIn = useIsDineIn();
  const isDoorDashOrder = orderType === EOrderTypes.DOORDASH_DRIVE;

  useOrderTimeChangeToast();
  useUnavailabilityAlert();
  useCheckoutRedirectToMenuCheck();
  if (isDineIn) return <CheckoutDinein />;

  return typeof isOnPremisePaymentOnly === 'undefined' ? (
    <Skeleton h={'100vh'} w={'100vw'} />
  ) : (
    <Checkout>
      <Heading
        as="h1"
        id="main-content"
        my={{ base: 4, md: 6 }}
        ml={{ base: 4, md: 0 }}
        fontSize={{ base: 24, md: 30 }}
        fontWeight="600"
      >
        Checkout
      </Heading>
      <SimpleGrid
        rowGap={SP}
        columnGap={{
          base: 0,
          md: SP,
        }}
        templateColumns={{
          base: 'repeat(1, 1fr)',
          md: 'repeat(3, 1fr)',
        }}
        pos="relative"
      >
        <AnimatePresence>
          <GridItem colSpan={2} key="main">
            <VStack spacing={SP} alignItems="stretch">
              <CheckoutOrderDetails showOrderNote={isOrderNoteEnabled} />

              {isOnPremisePaymentOnly && !isDoorDashOrder && <OnPremisePayment />}

              {!isScanAndPayPage && !isOnPremisePaymentOnly && (
                <CheckoutPayment
                  shouldShowPaymentMethod={shouldShowPaymentMethod}
                  shouldShowGiftCard={shouldShowGiftCard}
                  showEmailField={false}
                />
              )}
              {(!(isGooglePay || isApplePay) || giftCardCoversEntireOrderAmount) && (
                <CheckoutContactInfo isDisabled={disableContactInfo} />
              )}
            </VStack>
          </GridItem>
          {!isScanAndPayPage && (
            <CheckoutBagContainer>
              <ProductTotals includeTipInTotal includeGiftCard>
                {!isOnPremisePaymentOnly && <TipsSection withoutTitle />}
              </ProductTotals>
              <ApplyOffersButton />
              {!isMdDown && (
                <>
                  <CheckoutOrderButton />
                  <CheckoutDisclaimer />
                </>
              )}
            </CheckoutBagContainer>
          )}
        </AnimatePresence>
      </SimpleGrid>
      {shouldShowOrderButton && (
        <VStack mt={4} px={2} spacing={4} id="place-order-container">
          <CheckoutDisclaimer />
          <CheckoutOrderButton />
        </VStack>
      )}
    </Checkout>
  );
};
